import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditorRef } from 'platejs/react';
import { setTableColSize } from '@platejs/table';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import clsx from 'clsx';
import styles from './tableElements.module.scss';
import { useTableHover } from './tableHoverContext';
import { calculateSelectionRange, tableHasSpans } from './tableElementsUtils';

// Notion-style 6-dot drag handles for table rows and columns.
//
// Rendered ONCE per table (not per cell) and PORTALLED to document.body so
// they don't take up any DOM space around the table. The earlier wrapper-
// based approach reserved 26px of padding around the table for the handles,
// which collapsed into negative margin compensation — that worked at the
// editor root but pushed the table 26px past its container's left edge
// when nested inside a column-layout column, visually overlapping the
// column separator. Portal sidesteps the issue entirely: handles float on
// top of the document at viewport coordinates derived from the table's
// getBoundingClientRect().
//
// Hover state is supplied by TableHoverContext — each Cell calls
// show(rowIdx, colIdx) on mouseenter. Handles cancel the deferred hide on
// their own mouseenter so the pointer can travel from cell border to
// floating handle without flicker.
//
// CLICK → selects the entire row/column via the same Slate range-selection
// trick the right-click menu's "Select row/column" uses.
//
// DRAG → reorders the row/column using Slate's moveNodes. Column drag also
// swaps the persisted colSize so the moved column keeps its width in the
// new slot (via @platejs/table's setTableColSize). Rows have no equivalent
// persisted size mechanism; row height lives on the row node itself and
// moves automatically with moveNodes.

interface Props {
    tableElement: any;
    tableRef: React.RefObject<HTMLTableElement | null>;
}

interface DragState {
    axis: 'row' | 'column';
    sourceIndex: number;
    // Live target BOUNDARY index (between elements). 0 = before first,
    // length = after last. Used for the insertion indicator and to compute
    // the final moveNodes destination on drop.
    targetBoundary: number;
    // Pixel coord of the indicator line (in viewport coords) recomputed
    // every pointermove.
    indicatorPx: number;
    // Indicator length: table width for row drag, table height for column.
    indicatorLength: number;
}

const HANDLE_OFFSET = 22; // px outside the table edge
// Delay after the last scroll/resize before re-measuring handle positions.
const SCROLL_SETTLE_MS = 120;

// Snapshot/restore scroll positions of every scrollable ancestor + window so
// we can undo slate-react's implicit `Selection.scrollIntoView()` after a
// programmatic row/column select (focus point lands at the bottom cell).
export type ScrollSnapshot = Array<{ el: Element | Window; top: number; left: number }>;

// Exported for unit coverage (RECUR-26973) — pure DOM walk/restore, no
// component state. Behavior is unchanged; only visibility widened.
export function captureScrollAncestors(el: Element | null): ScrollSnapshot {
    const result: ScrollSnapshot = [];
    let cur: Element | null = el;
    let guard = 32;
    while (cur && guard-- > 0) {
        const cs = window.getComputedStyle(cur);
        const oy = cs.overflowY;
        const ox = cs.overflowX;
        if (oy === 'auto' || oy === 'scroll' || ox === 'auto' || ox === 'scroll') {
            result.push({ el: cur, top: cur.scrollTop, left: cur.scrollLeft });
        }
        cur = cur.parentElement;
    }
    result.push({ el: window, top: window.scrollY, left: window.scrollX });
    return result;
}

export function restoreScrollAncestors(snap: ScrollSnapshot) {
    for (const { el, top, left } of snap) {
        if (el === window) window.scrollTo({ left, top, behavior: 'instant' as ScrollBehavior });
        else {
            (el as Element).scrollTop = top;
            (el as Element).scrollLeft = left;
        }
    }
}

export function TableHandlesOverlay({ tableElement, tableRef }: Props) {
    const editor = useEditorRef();
    const { hovered, hideDeferred, cancelHide, clearImmediately } = useTableHover();
    const [dragState, setDragState] = useState<DragState | null>(null);
    const dragStateRef = useRef<DragState | null>(null);
    dragStateRef.current = dragState;

    // Re-measure on scroll/resize — handles use viewport coords. `tick` is
    // included in the positions useMemo deps so the recompute actually runs.
    const [tick, forceTick] = useState(0);
    // Hidden during an active scroll/resize burst. Chasing the table every
    // frame (forceTick → React re-render → re-measure the table + every row +
    // every cell) couldn't keep up on large docs and left the handle stranded
    // at a stale frame. Instead we hide while moving and re-measure once at rest.
    const [scrolling, setScrolling] = useState(false);
    useEffect(() => {
        if (hovered.row == null && hovered.col == null && !dragState) return;
        let settleId = 0;
        const onScrollOrResize = () => {
            // Keep handles live during a drag (the indicator must track); only
            // hide for plain scroll/resize.
            if (!dragStateRef.current) setScrolling(true);
            if (settleId) clearTimeout(settleId);
            settleId = window.setTimeout(() => {
                settleId = 0;
                setScrolling(false);
                forceTick((t) => t + 1); // re-measure at the settled position
            }, SCROLL_SETTLE_MS);
        };
        // capture:true catches scrolls on inner scroll containers too
        // (editor body, modal, etc.).
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            if (settleId) clearTimeout(settleId);
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [hovered.row, hovered.col, dragState]);

    // Resolve the table's slate path FRESH on each action. Caching the path
    // value goes stale: Slate keeps the table's node reference across edits
    // ABOVE it (structural sharing → this component never re-renders), yet the
    // table's index/path shifts. A cached path then points at the wrong node and
    // select/reorder silently no-op — the cause of "row/column handle selection
    // does nothing on some tables" (the ones below edited content). findPath
    // walks the live tree, so resolving at action time always returns the
    // current path.
    const getTablePath = useCallback((): number[] | null => {
        try {
            const path = (editor as any)?.api?.findPath?.(tableElement);
            return Array.isArray(path) ? path : null;
        } catch {
            return null;
        }
    }, [editor, tableElement]);

    // Read DOM rows/cells live each render. Tables are small; this is cheap
    // and avoids stale measurements if the cell DOM mutated.
    const rows = tableRef.current?.querySelectorAll<HTMLTableRowElement>(':scope > tbody > tr') ?? null;
    const rowCount = rows?.length ?? 0;
    const firstRowCells = rows?.[0]?.querySelectorAll<HTMLTableCellElement>(':scope > td, :scope > th') ?? null;
    const colCount = firstRowCells?.length ?? 0;

    // VIEWPORT-coordinate measurements (since handles are portalled to body
    // and use position: fixed). No wrapper offset arithmetic — straight from
    // getBoundingClientRect.
    const positions = useMemo(() => {
        if (!tableRef.current) return null;
        const tableRect = tableRef.current.getBoundingClientRect();
        const rowsArr = rows ? Array.from(rows) : [];
        const cellsArr = firstRowCells ? Array.from(firstRowCells) : [];
        return {
            tableLeft: tableRect.left,
            tableTop: tableRect.top,
            tableRight: tableRect.right,
            tableBottom: tableRect.bottom,
            tableWidth: tableRect.width,
            tableHeight: tableRect.height,
            rowCenters: rowsArr.map((r) => {
                const rr = r.getBoundingClientRect();
                return rr.top + rr.height / 2;
            }),
            rowEdges: [tableRect.top, ...rowsArr.map((r) => r.getBoundingClientRect().bottom)],
            colCenters: cellsArr.map((c) => {
                const cr = c.getBoundingClientRect();
                return cr.left + cr.width / 2;
            }),
            colEdges: [tableRect.left, ...cellsArr.map((c) => c.getBoundingClientRect().right)],
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hovered.row, hovered.col, dragState, rowCount, colCount, tableRef, tick]);

    const selectAxis = useCallback(
        (axis: 'row' | 'column', index: number) => {
            const tablePath = getTablePath();
            if (!tablePath) return;
            try {
                const ed: any = editor;
                const tableNode: any = ed?.api?.node?.(tablePath)?.[0];
                const { start: startPath, end: endPath } = calculateSelectionRange(axis, tableNode, tablePath, index);
                if (!startPath || !endPath) return;
                const start = ed?.api?.start?.(startPath);
                const end = ed?.api?.end?.(endPath);
                if (start && end) {
                    // Snapshot scrolls so slate-react's auto-scroll-into-view
                    // (triggered by the select+focus below) doesn't jump the
                    // viewport to the column's last cell.
                    const scrolls = captureScrollAncestors(tableRef.current);
                    ed?.tf?.select?.({ anchor: start, focus: end });
                    ed?.tf?.focus?.();
                    requestAnimationFrame(() => restoreScrollAncestors(scrolls));
                }
            } catch {
                /* row/column select failed */
            }
        },
        [editor, getTablePath, tableRef],
    );

    const pointerToBoundary = useCallback(
        (axis: 'row' | 'column', clientX: number, clientY: number): number => {
            if (axis === 'row') {
                const centers = positions?.rowCenters ?? [];
                let boundary = centers.length;
                for (let i = 0; i < centers.length; i++) {
                    if (clientY < centers[i]) {
                        boundary = i;
                        break;
                    }
                }
                return boundary;
            } else {
                const centers = positions?.colCenters ?? [];
                let boundary = centers.length;
                for (let i = 0; i < centers.length; i++) {
                    if (clientX < centers[i]) {
                        boundary = i;
                        break;
                    }
                }
                return boundary;
            }
        },
        [positions],
    );

    const boundaryToPx = useCallback(
        (axis: 'row' | 'column', boundary: number): number => {
            if (axis === 'row') {
                const edges = positions?.rowEdges ?? [];
                return edges[Math.min(boundary, edges.length - 1)] ?? 0;
            } else {
                const edges = positions?.colEdges ?? [];
                return edges[Math.min(boundary, edges.length - 1)] ?? 0;
            }
        },
        [positions],
    );

    const commitReorder = useCallback(
        (axis: 'row' | 'column', src: number, boundary: number) => {
            const tablePath = getTablePath();
            if (!tablePath) return;
            if (boundary === src || boundary === src + 1) return; // no-op
            const dst = boundary > src ? boundary - 1 : boundary;
            const ed: any = editor;
            try {
                if (axis === 'row') {
                    ed?.tf?.moveNodes?.({ at: [...tablePath, src], to: [...tablePath, dst] });
                } else {
                    const tableNode: any = ed?.api?.node?.(tablePath)?.[0];
                    const rowCountLocal = tableNode?.children?.length ?? 0;
                    const colSizes: number[] = Array.isArray(tableNode?.colSizes) ? [...tableNode.colSizes] : [];
                    ed?.tf?.withoutNormalizing?.(() => {
                        for (let r = 0; r < rowCountLocal; r++) {
                            ed?.tf?.moveNodes?.({ at: [...tablePath, r, src], to: [...tablePath, r, dst] });
                        }
                    });
                    if (colSizes.length > 0) {
                        const [moved] = colSizes.splice(src, 1);
                        colSizes.splice(dst, 0, moved);
                        // `{ at: tablePath }` pins the transform to THIS
                        // table — without it the lib falls back to "table
                        // above editor selection", which during a drag may
                        // be undefined or wrong.
                        for (let i = 0; i < colSizes.length; i++) {
                            const w = colSizes[i];
                            if (typeof w === 'number' && w > 0) {
                                try {
                                    (setTableColSize as any)(ed, { colIndex: i, width: w }, { at: tablePath });
                                } catch {
                                    /* per-slot defensive */
                                }
                            }
                        }
                    }
                }
            } catch {
                /* row/column reorder failed */
            }
        },
        [editor, getTablePath],
    );

    useEffect(() => {
        if (!dragState) return;
        const onMove = (e: PointerEvent) => {
            const ds = dragStateRef.current;
            if (!ds) return;
            const boundary = pointerToBoundary(ds.axis, e.clientX, e.clientY);
            const indicatorPx = boundaryToPx(ds.axis, boundary);
            setDragState((prev) => (prev ? { ...prev, targetBoundary: boundary, indicatorPx } : prev));
        };
        const onUp = (e: PointerEvent) => {
            const ds = dragStateRef.current;
            if (!ds) return;
            const boundary = pointerToBoundary(ds.axis, e.clientX, e.clientY);
            commitReorder(ds.axis, ds.sourceIndex, boundary);
            setDragState(null);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            clearImmediately();
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
    }, [dragState, pointerToBoundary, boundaryToPx, commitReorder, clearImmediately]);

    const startDrag = useCallback(
        (axis: 'row' | 'column', index: number, e: React.PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            selectAxis(axis, index);
            const boundary = pointerToBoundary(axis, e.clientX, e.clientY);
            setDragState({
                axis,
                sourceIndex: index,
                targetBoundary: boundary,
                indicatorPx: boundaryToPx(axis, boundary),
                indicatorLength: axis === 'row' ? positions?.tableWidth ?? 0 : positions?.tableHeight ?? 0,
            });
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
        },
        [selectAxis, pointerToBoundary, boundaryToPx, positions],
    );

    if (!positions || rowCount === 0 || colCount === 0) return null;
    if (typeof document === 'undefined') return null;

    // Clip handles to the viewport — without this they hover over drawer
    // chrome when the table scrolls off-screen.
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    const tableOnScreen = positions.tableBottom > 0 && positions.tableTop < vh;
    if (!tableOnScreen && !dragState) return null;

    // Mid-scroll: keep the drag indicator (if dragging) but hide the hover
    // handles rather than render them at a stale, lagging position.
    if (scrolling && !dragState) return null;

    // F4 (RECUR-26973): commitReorder's column-swap loop moves each row's
    // child at the SAME structural index, which stops matching the visual
    // column once colSpan/rowSpan exist (import/paste can produce merged
    // tables even though in-editor merging is disabled) — dragging one
    // reorders the grid into a corrupted shape. Reordering THROUGH a span
    // anchor is ambiguous anyway (Word/Sheets disable it too), so suppress
    // the drag affordance entirely on any merged table rather than build a
    // partial fix. Future alternative: map the pointer to a VISUAL column via
    // the occupancy grid tableElementsUtils already builds for
    // calculateRectRange, and allow reorder around (not through) a span.
    const hasSpans = tableHasSpans(tableElement);
    const showColHandle = hovered.col != null && hovered.col < colCount && !dragState && !hasSpans;
    const showRowHandle = hovered.row != null && hovered.row < rowCount && !dragState && !hasSpans;

    const colHandleTop = positions.tableTop - HANDLE_OFFSET;
    const colHandleVisible = showColHandle && colHandleTop > 0 && colHandleTop < vh;

    const rowCenter = hovered.row != null ? positions.rowCenters[hovered.row] : undefined;
    const rowHandleVisible = showRowHandle && rowCenter != null && rowCenter > 0 && rowCenter < vh;

    const colHandle =
        colHandleVisible && positions.colCenters[hovered.col!] != null ? (
            <button
                type="button"
                className={clsx(styles.tableHandle, styles.tableHandleCol)}
                style={{
                    position: 'fixed',
                    left: positions.colCenters[hovered.col!] - 14,
                    top: positions.tableTop - HANDLE_OFFSET,
                }}
                onMouseEnter={cancelHide}
                onMouseLeave={hideDeferred}
                onClick={(e) => {
                    e.preventDefault();
                    selectAxis('column', hovered.col!);
                }}
                onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    if (colCount > 1) startDrag('column', hovered.col!, e);
                }}
                title="Click to select column · Drag to reorder"
                aria-label={`Column ${hovered.col! + 1} handle`}
            >
                <DragIndicatorIcon style={{ fontSize: 16, transform: 'rotate(90deg)' }} />
            </button>
        ) : null;

    const rowHandle =
        rowHandleVisible && positions.rowCenters[hovered.row!] != null ? (
            <button
                type="button"
                className={clsx(styles.tableHandle, styles.tableHandleRow)}
                style={{
                    position: 'fixed',
                    left: positions.tableLeft - HANDLE_OFFSET,
                    top: positions.rowCenters[hovered.row!] - 14,
                }}
                onMouseEnter={cancelHide}
                onMouseLeave={hideDeferred}
                onClick={(e) => {
                    e.preventDefault();
                    selectAxis('row', hovered.row!);
                }}
                onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    if (rowCount > 1) startDrag('row', hovered.row!, e);
                }}
                title="Click to select row · Drag to reorder"
                aria-label={`Row ${hovered.row! + 1} handle`}
            >
                <DragIndicatorIcon style={{ fontSize: 16 }} />
            </button>
        ) : null;

    const indicator =
        dragState != null ? (
            <div
                className={clsx(
                    styles.tableDropIndicator,
                    dragState.axis === 'row' ? styles.tableDropIndicatorRow : styles.tableDropIndicatorCol,
                )}
                style={
                    dragState.axis === 'row'
                        ? {
                              position: 'fixed',
                              left: positions.tableLeft,
                              top: dragState.indicatorPx - 1,
                              width: positions.tableWidth,
                              height: 2,
                          }
                        : {
                              position: 'fixed',
                              left: dragState.indicatorPx - 1,
                              top: positions.tableTop,
                              width: 2,
                              height: positions.tableHeight,
                          }
                }
            />
        ) : null;

    return createPortal(
        <>
            {colHandle}
            {rowHandle}
            {indicator}
        </>,
        document.body,
    );
}
